// src/components/AmbientGradient.jsx
//
// ODFTrades — Ambient background layer.
//
// Renders a fixed, full-screen, pointer-events-none atmospheric
// backdrop sitting behind ALL page content. Two layers stacked:
//
//   1) CSS mesh gradient (always on) — multiple drifting radial
//      gradients in brand colors. Bulletproof, zero runtime cost.
//   2) WebGL shader gradient (lazy-loaded) — @shadergradient/react,
//      adds living motion + depth. If it fails to load for any
//      reason, layer 1 alone still looks premium.
//
// Mounted ONCE at the App root so every page (BrandHome, Landing,
// Legal, Resources, etc.) gets the same atmosphere. The logged-in
// dashboard areas keep their own solid background via z-index.
//
import React from 'react';

// NOTE: ShaderGradient (WebGL) is intentionally disabled here. The
// brand homepage already mounts a Hero3D R3F canvas, and stacking
// multiple WebGL contexts in the background causes context-loss
// glitches in some browsers. The CSS mesh layer below is rich
// enough to carry the ambient layer on its own.

// ── CSS mesh gradient base layer ──────────────────────────────────
// Near-pure black with two VERY subtle violet hints (≤3% opacity)
// for atmospheric depth. Reads as black; only catches the eye on
// large screens / dark rooms. z-index:-1 keeps it strictly behind
// in-flow content inside #root's stacking context.
function MeshLayer() {
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        background: `
          radial-gradient(80% 60% at 85% 12%, rgba(70, 50, 180, 0.05), transparent 70%),
          radial-gradient(70% 55% at 12% 92%, rgba(40, 30, 120, 0.04), transparent 70%),
          #0a0a0f
        `,
      }}
    />
  );
}

export default function AmbientGradient() {
  // Skip the ambient layer on touch devices. position:fixed
  // elements get repainted on every scroll frame on iOS Safari,
  // which can make scroll feel sluggish — especially over long
  // forms like the BetaGate waitlist. Mobile already gets a
  // pure black body bg from index.css, which looks identical to
  // the desktop ambient layer at its low (~4%) opacity.
  const isTouch =
    typeof window !== 'undefined' &&
    (
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
      (window.matchMedia &&
        (window.matchMedia('(pointer: coarse)').matches ||
          window.matchMedia('(hover: none)').matches))
    );
  if (isTouch) return null;

  return <MeshLayer />;
}
